import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Twitter, Linkedin, Facebook, Instagram, Youtube, MessageCircle, Mail, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import ContactForm from "./ContactForm";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, apiError } from "../lib/api";
import { trackEvent } from "../lib/consent";
import { useSite } from "../contexts/SiteContext";
import { socialName } from "../lib/format";

const SOCIAL_ICONS = { twitter: Twitter, linkedin: Linkedin, facebook: Facebook, instagram: Instagram, youtube: Youtube, whatsapp: MessageCircle };
// Le nom lisible d'un compte, pour que le lien annonce ou il mene.
const poignee = (url) => {
  const s = String(url || "").replace(/\/+$/, "");
  if (s.includes("wa.me/")) return "+" + s.split("wa.me/")[1];
  const bout = s.split("/").pop();
  return bout ? "@" + bout.replace(/^@/, "") : "";
};

export default function Footer() {
  const { t } = useTranslation();
  const { social = {}, legal = {} } = useSite();
  const socialLinks = Object.entries(SOCIAL_ICONS).filter(([k]) => social[k]);
  // WhatsApp a sa place a part : c'est le canal qu'on ouvre vraiment ici.
  const autres = socialLinks.filter(([k]) => k !== "whatsapp");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      await api.post("/newsletter/subscribe", { email: email.trim() });
      trackEvent("newsletter_subscribe");
      toast.success(t("footer.newsletter_thanks"));
      setEmail("");
    } catch (err) { toast.error(apiError(err)); } finally { setSubscribing(false); }
  };
  return (
    <footer className="border-t border-border bg-[hsl(var(--secondary))]/60 mt-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12">
        <div className="grid gap-6 lg:grid-cols-5 mb-12">
          <div id="contact" className="lg:col-span-3 rounded-xl border border-border bg-card p-6 scroll-mt-24">
            <ContactForm />
          </div>
          <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6 space-y-5" data-testid="footer-reach">
            <div>
              <h3 className="font-display text-lg font-semibold">{t("footer.reach_title")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("footer.reach_sub")}</p>
            </div>
            {social.whatsapp && (
              <Button asChild className="w-full bg-[hsl(var(--accent))] text-white" data-testid="footer-whatsapp">
                <a href={social.whatsapp} target="_blank" rel="noreferrer">
                  <MessageCircle className="h-4 w-4 mr-2" /> {t("footer.whatsapp_cta")}
                </a>
              </Button>
            )}
            <ul className="space-y-2.5">
              {autres.map(([k, Icon]) => (
                <li key={k}>
                  <a href={social[k]} target="_blank" rel="noreferrer" data-testid={`footer-reach-${k}`}
                    className="group flex items-center gap-3 text-sm text-foreground/80 hover:text-foreground">
                    <span className="h-8 w-8 rounded-md border border-border flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
                    <span className="flex-1 min-w-0">
                      <span>{socialName(k)}</span>
                      <span className="block text-xs text-muted-foreground truncate">{poignee(social[k])}</span>
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </a>
                </li>
              ))}
              {legal.email && (
                <li>
                  <a href={`mailto:${legal.email}`} className="group flex items-center gap-3 text-sm text-foreground/80 hover:text-foreground" data-testid="footer-reach-email">
                    <span className="h-8 w-8 rounded-md border border-border flex items-center justify-center shrink-0"><Mail className="h-4 w-4" /></span>
                    <span className="flex-1 min-w-0">
                      <span>{t("footer.reach_email")}</span>
                      <span className="block text-xs text-muted-foreground truncate">{legal.email}</span>
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-12 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-3">
          <Logo />
          <p className="text-sm text-muted-foreground max-w-xs">{t("brand.tagline")}</p>
        </div>
        <div className="space-y-3">
          <h4 className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{t("nav.programs")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/opportunites" className="hover:text-foreground text-foreground/80">{t("nav.opportunities")}</Link></li>
            <li><Link to="/founders" className="hover:text-foreground text-foreground/80">{t("nav.founders")}</Link></li>
            <li><Link to="/community" className="hover:text-foreground text-foreground/80">{t("nav.community")}</Link></li>
          </ul>
        </div>
        <div className="space-y-3">
          <h4 className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{t("nav.investors")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/investors" className="hover:text-foreground text-foreground/80">{t("nav.investors")}</Link></li>
            <li><Link to="/sme" className="hover:text-foreground text-foreground/80">{t("nav.sme")}</Link></li>
            <li><Link to="/about" className="hover:text-foreground text-foreground/80">{t("nav.about")}</Link></li>
          </ul>
        </div>
        <div className="space-y-3">
          <h4 className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{t("footer.newsletter_title")}</h4>
          <p className="text-sm text-muted-foreground">{t("footer.newsletter_sub")}</p>
          <form className="flex gap-2" onSubmit={subscribe}>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="newsletter-email" />
            <Button type="submit" disabled={subscribing} className="bg-[hsl(var(--primary))] text-white" data-testid="newsletter-subscribe">{subscribing ? "…" : t("footer.subscribe")}</Button>
          </form>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs" data-testid="footer-legal">
          <Link to="/legal" className="text-muted-foreground hover:text-foreground">{t("footer.legal_notice")}</Link>
          <span className="text-border">·</span>
          <Link to="/data-protection" className="text-muted-foreground hover:text-foreground">{t("footer.data_protection")}</Link>
          <span className="text-border">·</span>
          <Link to="/registration-info" className="text-muted-foreground hover:text-foreground">{t("footer.registration_info")}</Link>
          <span className="text-border">·</span>
          <Link to="/accessibility" className="text-muted-foreground hover:text-foreground">{t("footer.accessibility")}</Link>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>{t("common.copyright")}</span>
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-3" data-testid="footer-social">
              {socialLinks.map(([k, Icon]) => (
                <a key={k} href={social[k]} target="_blank" rel="noreferrer" aria-label={k} className="text-muted-foreground hover:text-foreground transition-colors">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
          <span>{t("common.made_with")}</span>
        </div>
      </div>
    </footer>
  );
}
